/**
 * Report 1: Product Stock, Sales & Cost
 *
 * One row per product. Shows current stock position, stock value,
 * sales activity in the chosen period, unit cost and COGS.
 *
 * Data sources:
 *   - catalogMock.ts   → product master (SKU, name, category, standardCostCents)
 *   - inventoryMock.ts → stock movements (on-hand per product)
 *   - invoicingMock.ts → invoice lines (period sales qty + revenue)
 *   - salesMock.ts     → sales order lines (supplementary period sales)
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  ArrowUpDown,
  BarChart3,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { Badge, DataTable, Input, KpiCard, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { printElement } from "../../../lib/export";
import {
  PeriodSelector,
  defaultPeriodWindow,
  type PeriodWindow,
} from "../../../components/gl/PeriodSelector";
import { useAppSettings } from "../../../lib/appSettings";

import {
  mockProducts as catalogProducts,
  mockCategories,
  mockUnits,
} from "../../../lib/catalogMock";
import {
  mockStockMovements,
  mockStockReservations,
} from "../../../lib/inventoryMock";
import {
  mockInvoices,
  mockInvoiceLines,
} from "../../../lib/invoicingMock";

export const Route = createFileRoute(
  "/_app/reports/product-stock-sales-cost",
)({
  component: ProductStockSalesCostPage,
});

/* ─────────────────────────────────────────────────────────────
 * Types
 * ───────────────────────────────────────────────────────────── */

type StatusLabel = "In Stock" | "Low" | "Out";
type SortKey =
  | "sku"
  | "name"
  | "category"
  | "stockQty"
  | "stockValue"
  | "salesQty"
  | "salesRevenue"
  | "unitCost"
  | "cogs"
  | "status";

type ProductRow = {
  productId: string;
  sku: string;
  name: string;
  category: string;
  unit: string;
  stockQty: number;
  stockValueCents: bigint;
  salesQty: number;
  salesRevenueCents: bigint;
  unitCostCents: bigint | null;
  cogsCents: bigint | null;
  isActive: boolean;
  reorderQuantity: number | null;
  status: StatusLabel;
};

/* ─────────────────────────────────────────────────────────────
 * Aggregation helpers
 * ───────────────────────────────────────────────────────────── */

function computeStockQtyByProduct(): Map<string, number> {
  const m = new Map<string, number>();
  for (const mv of mockStockMovements) {
    m.set(mv.productId, (m.get(mv.productId) ?? 0) + mv.quantityDelta);
  }
  return m;
}

function computeReservedByProduct(): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of mockStockReservations) {
    if (r.status !== "active") continue;
    m.set(r.productId, (m.get(r.productId) ?? 0) + r.quantityReserved);
  }
  return m;
}

function computeSalesByProduct(
  from: number,
  to: number,
): Map<string, { qty: number; revenueCents: bigint }> {
  const m = new Map<string, { qty: number; revenueCents: bigint }>();

  // Collect valid invoice ids in window (exclude void + draft).
  const validInvoiceIds = new Set(
    mockInvoices
      .filter(
        (inv) =>
          inv.status !== "void" &&
          inv.status !== "draft" &&
          inv.issueDate >= from &&
          inv.issueDate <= to,
      )
      .map((inv) => inv._id),
  );

  for (const line of mockInvoiceLines) {
    if (!validInvoiceIds.has(line.invoiceId)) continue;
    const prev = m.get(line.productId) ?? { qty: 0, revenueCents: 0n };
    m.set(line.productId, {
      qty: prev.qty + line.quantity,
      revenueCents: prev.revenueCents + line.lineTotalInBaseCents,
    });
  }
  return m;
}

function stockStatus(
  qty: number,
  reorderQty: number | null,
): StatusLabel {
  if (qty <= 0) return "Out";
  if (reorderQty !== null && qty <= reorderQty * 0.3) return "Low";
  return "In Stock";
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function ProductStockSalesCostPage() {
  const { companyProfile } = useAppSettings();

  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "discontinued"
  >("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("stockValue");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const printRef = useRef<HTMLDivElement>(null);

  /* ─── Lookup maps ─── */
  const catById = useMemo(
    () => new Map(mockCategories.map((c) => [c._id, c.displayName])),
    [],
  );
  const unitById = useMemo(
    () => new Map(mockUnits.map((u) => [u._id, u.code])),
    [],
  );

  /* ─── Stock + sales aggregation ─── */
  const stockQtyByProduct = useMemo(computeStockQtyByProduct, []);
  const reservedByProduct = useMemo(computeReservedByProduct, []);
  const salesByProduct = useMemo(
    () => computeSalesByProduct(period.fromDate, period.toDate),
    [period],
  );

  /* ─── All rows ─── */
  const allRows = useMemo<ProductRow[]>(() => {
    return catalogProducts.map((p) => {
      const rawQty = stockQtyByProduct.get(p._id) ?? 0;
      const reserved = reservedByProduct.get(p._id) ?? 0;
      const stockQty = Math.max(0, rawQty - reserved);
      const unitCostCents: bigint | null =
        "standardCostCents" in p
          ? (p.standardCostCents as bigint)
          : null;
      const stockValueCents =
        unitCostCents != null
          ? BigInt(Math.max(0, stockQty)) * unitCostCents
          : 0n;

      const sales = salesByProduct.get(p._id);
      const salesQty = sales?.qty ?? 0;
      const salesRevenueCents = sales?.revenueCents ?? 0n;
      const cogsCents =
        unitCostCents != null
          ? BigInt(salesQty) * unitCostCents
          : null;

      return {
        productId: p._id,
        sku: p.sku,
        name: p.displayName,
        category: p.categoryId ? (catById.get(p.categoryId) ?? "—") : "—",
        unit: unitById.get(p.primaryUnitId) ?? p.primaryUnitId,
        stockQty,
        stockValueCents,
        salesQty,
        salesRevenueCents,
        unitCostCents,
        cogsCents,
        isActive: p.isActive,
        reorderQuantity: p.reorderQuantity ?? null,
        status: stockStatus(stockQty, p.reorderQuantity ?? null),
      };
    });
  }, [catById, unitById, stockQtyByProduct, reservedByProduct, salesByProduct]);

  /* ─── Filtered + sorted rows ─── */
  const rows = useMemo(() => {
    let r = allRows;

    if (statusFilter === "active") r = r.filter((x) => x.isActive);
    if (statusFilter === "discontinued") r = r.filter((x) => !x.isActive);

    if (categoryFilter !== "all")
      r = r.filter((x) => x.category === categoryFilter);

    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (x) =>
          x.sku.toLowerCase().includes(q) ||
          x.name.toLowerCase().includes(q),
      );
    }

    r = [...r].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "sku":
          cmp = a.sku.localeCompare(b.sku);
          break;
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "category":
          cmp = a.category.localeCompare(b.category);
          break;
        case "stockQty":
          cmp = a.stockQty - b.stockQty;
          break;
        case "stockValue":
          cmp =
            a.stockValueCents < b.stockValueCents
              ? -1
              : a.stockValueCents > b.stockValueCents
                ? 1
                : 0;
          break;
        case "salesQty":
          cmp = a.salesQty - b.salesQty;
          break;
        case "salesRevenue":
          cmp =
            a.salesRevenueCents < b.salesRevenueCents
              ? -1
              : a.salesRevenueCents > b.salesRevenueCents
                ? 1
                : 0;
          break;
        case "unitCost":
          cmp =
            (a.unitCostCents ?? 0n) < (b.unitCostCents ?? 0n)
              ? -1
              : (a.unitCostCents ?? 0n) > (b.unitCostCents ?? 0n)
                ? 1
                : 0;
          break;
        case "cogs":
          cmp =
            (a.cogsCents ?? 0n) < (b.cogsCents ?? 0n)
              ? -1
              : (a.cogsCents ?? 0n) > (b.cogsCents ?? 0n)
                ? 1
                : 0;
          break;
        case "status":
          cmp = a.status.localeCompare(b.status);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return r;
  }, [allRows, statusFilter, categoryFilter, search, sortKey, sortDir]);

  /* ─── Totals ─── */
  const totals = useMemo(() => {
    let totalStockValue = 0n;
    let totalSalesQty = 0;
    let totalRevenue = 0n;
    let totalCogs = 0n;
    let hasCogs = false;
    for (const r of rows) {
      totalStockValue += r.stockValueCents;
      totalSalesQty += r.salesQty;
      totalRevenue += r.salesRevenueCents;
      if (r.cogsCents != null) {
        totalCogs += r.cogsCents;
        hasCogs = true;
      }
    }
    return { totalStockValue, totalSalesQty, totalRevenue, totalCogs, hasCogs };
  }, [rows]);

  /* ─── KPIs ─── */
  const kpis = useMemo(() => {
    const bestSelling = allRows.reduce<ProductRow | null>(
      (best, r) => (!best || r.salesQty > best.salesQty ? r : best),
      null,
    );
    const lowestStock = allRows
      .filter((r) => r.stockQty > 0)
      .reduce<ProductRow | null>(
        (low, r) => (!low || r.stockQty < low.stockQty ? r : low),
        null,
      );
    return {
      totalProducts: allRows.length,
      totalStockValue: allRows.reduce((s, r) => s + r.stockValueCents, 0n),
      bestSelling: bestSelling?.name ?? "—",
      lowestStock: lowestStock?.name ?? "—",
    };
  }, [allRows]);

  /* ─── Category list for filter ─── */
  const categoryOptions = useMemo(() => {
    const s = new Set(allRows.map((r) => r.category).filter((c) => c !== "—"));
    return Array.from(s).sort();
  }, [allRows]);

  /* ─── Sort toggle helper ─── */
  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col)
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ChevronUp className="ml-1 h-3 w-3" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3" />
    );
  }

  /* ─── Export ─── */
  type CsvRow = {
    sku: string;
    name: string;
    category: string;
    unit: string;
    stockQty: number;
    stockValueCents: bigint;
    salesQty: number;
    salesRevenueCents: bigint;
    unitCostCents: string;
    cogsCents: string;
    status: string;
  };

  function buildCsvRows(): CsvRow[] {
    const data: CsvRow[] = rows.map((r) => ({
      sku: r.sku,
      name: r.name,
      category: r.category,
      unit: r.unit,
      stockQty: r.stockQty,
      stockValueCents: r.stockValueCents,
      salesQty: r.salesQty,
      salesRevenueCents: r.salesRevenueCents,
      unitCostCents:
        r.unitCostCents != null
          ? (Number(r.unitCostCents) / 100).toFixed(2)
          : "—",
      cogsCents:
        r.cogsCents != null
          ? (Number(r.cogsCents) / 100).toFixed(2)
          : "—",
      status: r.status,
    }));
    // Footer total row.
    data.push({
      sku: "TOTAL",
      name: "",
      category: "",
      unit: "",
      stockQty: rows.reduce((s, r) => s + r.stockQty, 0),
      stockValueCents: totals.totalStockValue,
      salesQty: totals.totalSalesQty,
      salesRevenueCents: totals.totalRevenue,
      unitCostCents: "",
      cogsCents: totals.hasCogs
        ? (Number(totals.totalCogs) / 100).toFixed(2)
        : "—",
      status: "",
    });
    return data;
  }

  const CSV_COLUMNS: ExportColumn<CsvRow>[] = [
    { key: "sku", label: "SKU" },
    { key: "name", label: "Product" },
    { key: "category", label: "Category" },
    { key: "unit", label: "Unit" },
    {
      key: "stockQty",
      label: "Stock Qty",
      format: (v) => String(v),
    },
    { key: "stockValueCents", label: "Stock Value (BDT)" },
    {
      key: "salesQty",
      label: "Sales Qty",
      format: (v) => String(v),
    },
    { key: "salesRevenueCents", label: "Sales Revenue (BDT)" },
    {
      key: "unitCostCents",
      label: "Unit Cost (BDT)",
      format: (v) => String(v),
    },
    {
      key: "cogsCents",
      label: "COGS (BDT)",
      format: (v) => String(v),
    },
    { key: "status", label: "Status" },
  ];

  function handleCsv(ext: "csv" | "xls") {
    downloadCsv(
      exportFilename("product-stock-sales-cost", ext),
      toCsv(buildCsvRows(), CSV_COLUMNS),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Product Stock, Sales & Cost",
      company: companyProfile.displayName,
      subtitle: `Period: ${new Date(period.fromDate).toLocaleDateString("en-BD")} – ${new Date(period.toDate).toLocaleDateString("en-BD")}`,
    });
  }

  /* ─── Table columns ─── */
  const columns = useMemo<ColumnDef<ProductRow>[]>(
    () => [
      {
        id: "sku",
        header: () => (
          <button
            type="button"
            className="flex items-center text-left font-medium"
            onClick={() => toggleSort("sku")}
          >
            SKU <SortIcon col="sku" />
          </button>
        ),
        cell: ({ row }) => (
          <code className="font-mono text-[12px] text-(--text-secondary) bg-(--bg-hover) rounded px-1.5 py-0.5">
            {row.original.sku}
          </code>
        ),
      },
      {
        id: "name",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("name")}
          >
            Product <SortIcon col="name" />
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-(--text-primary) font-medium">
            {row.original.name}
          </span>
        ),
      },
      {
        id: "category",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("category")}
          >
            Category <SortIcon col="category" />
          </button>
        ),
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">{row.original.category}</span>
        ),
      },
      {
        id: "stockQty",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("stockQty")}
          >
            Stock <SortIcon col="stockQty" />
          </button>
        ),
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.stockQty.toLocaleString("en-BD")}{" "}
            <span className="text-(--text-tertiary) text-[11px]">
              {row.original.unit}
            </span>
          </span>
        ),
      },
      {
        id: "stockValue",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("stockValue")}
          >
            Stock Value <SortIcon col="stockValue" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.stockValueCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "salesQty",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("salesQty")}
          >
            Sales Qty <SortIcon col="salesQty" />
          </button>
        ),
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.salesQty > 0
              ? row.original.salesQty.toLocaleString("en-BD")
              : <span className="text-(--text-tertiary)">—</span>}
          </span>
        ),
      },
      {
        id: "salesRevenue",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("salesRevenue")}
          >
            Revenue <SortIcon col="salesRevenue" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {row.original.salesRevenueCents > 0n
              ? formatMoney(row.original.salesRevenueCents, "BDT", {
                  locale: "en-BD",
                })
              : <span className="text-(--text-tertiary)">—</span>}
          </span>
        ),
      },
      {
        id: "unitCost",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("unitCost")}
          >
            Unit Cost <SortIcon col="unitCost" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          row.original.unitCostCents != null ? (
            <span className="font-mono tabular-nums">
              {formatMoney(row.original.unitCostCents, "BDT", {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "cogs",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("cogs")}
          >
            COGS <SortIcon col="cogs" />
          </button>
        ),
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          row.original.cogsCents != null ? (
            <span className="font-mono tabular-nums">
              {row.original.cogsCents > 0n
                ? formatMoney(row.original.cogsCents, "BDT", {
                    locale: "en-BD",
                  })
                : <span className="text-(--text-tertiary)">—</span>}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "status",
        header: () => (
          <button
            type="button"
            className="flex items-center font-medium"
            onClick={() => toggleSort("status")}
          >
            Status <SortIcon col="status" />
          </button>
        ),
        cell: ({ row }) => {
          const s = row.original.status;
          return (
            <Badge
              variant={
                s === "In Stock"
                  ? "success"
                  : s === "Low"
                    ? "warning"
                    : "danger"
              }
              size="sm"
            >
              {s}
            </Badge>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortKey, sortDir],
  );

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Product Stock, Sales & Cost"
        description="Current stock position, stock value and period sales per product."
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      {/* KPI strip */}
      <section
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
        aria-label="Stock & sales KPIs"
      >
        <KpiCard
          caption="Total Products"
          value={String(kpis.totalProducts)}
          hint={`${allRows.filter((r) => r.isActive).length} active`}
        />
        <KpiCard
          caption="Total Stock Value"
          value={formatMoneySmart(kpis.totalStockValue, "BDT", {
            locale: "en-BD",
          })}
          hint="at standard cost"
        />
        <KpiCard
          caption="Best-Selling"
          value={kpis.bestSelling}
          hint="by qty in period"
          icon={<BarChart3 className="h-4 w-4" aria-hidden />}
        />
        <KpiCard
          caption="Lowest Stock"
          value={kpis.lowestStock}
          hint="by qty on hand"
        />
      </section>

      {/* Period selector + filters */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-(--text-tertiary)">
            Sales period
          </span>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status chips */}
        {(
          [
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "discontinued", label: "Discontinued" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setStatusFilter(opt.key)}
            className={cn(
              "h-7 rounded-full border px-3 text-[11px] transition-colors",
              statusFilter === opt.key
                ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
                : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
            )}
          >
            {opt.label}
          </button>
        ))}

        {/* Category dropdown */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-7 rounded-full border border-(--border-subtle) bg-(--bg-canvas) px-3 text-[11px] text-(--text-secondary) focus:outline-none focus:ring-1 focus:ring-(--accent)"
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        {/* Search box */}
        <Input
          placeholder="Search SKU or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 w-52 text-[12px]"
          aria-label="Search products"
        />
      </div>

      {/* Table */}
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r.productId}
        emptyTitle="No products match"
        emptyDescription="Try adjusting your filters or search query."
        emptyIcon={<BarChart3 className="h-5 w-5" aria-hidden />}
      />

      {/* Aggregate footer */}
      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-3 text-[13px]">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Rows
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {rows.length}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total Stock Value
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {formatMoney(totals.totalStockValue, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total Sales Qty
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {totals.totalSalesQty.toLocaleString("en-BD")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total Revenue
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {formatMoney(totals.totalRevenue, "BDT", { locale: "en-BD" })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-(--text-tertiary) mb-0.5">
              Total COGS
            </div>
            <div className="font-mono tabular-nums font-semibold text-(--text-primary)">
              {totals.hasCogs
                ? formatMoney(totals.totalCogs, "BDT", { locale: "en-BD" })
                : "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
